import React from 'react';
import { Car } from 'lucide-react';
import { VehicleOwnershipStep } from './VehicleOwnershipStep';

interface VehicleInfo {
  make: string;
  model: string;
  ownership: {
    isOwner: string;
    isKeeper: string;
    purchaseDate: string;
    notPurchased: boolean;
    parkingLocation: string;
  };
  transmission: string;
  drivePosition: string;
  seats: string;
  alarm: string;
  tracker: string;
  year: string;
  value: string;
  registration: string;
  mileage: string;
  isModified: string;
}

interface VehicleInfoStepProps {
  data: VehicleInfo;
  onInputChange: (field: string, value: string) => void;
  isLookingUpVehicle: boolean;
  handleVehicleLookup: (registration: string) => Promise<void>;
}

export function VehicleInfoStep({ 
  data, 
  onInputChange, 
  isLookingUpVehicle, 
  handleVehicleLookup 
}: VehicleInfoStepProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <Car className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Vehicle Details</h2>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Registration Number
        </label>
        <input
          type="text"
          placeholder="Registration Number"
          value={data.registration}
          onChange={(e) => onInputChange('registration', e.target.value.toUpperCase())}
          className="w-full p-2 border rounded uppercase"
          required
        />
      </div>
      <input
        value={data.make}
        onChange={(e) => onInputChange('make', e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="Car Make"
        required
      />
      <input
        type="text"
        placeholder="Car Model"
        value={data.model}
        onChange={(e) => onInputChange('model', e.target.value)}
        className="w-full p-2 border rounded"
        required
      />
      <input
        value={data.year}
        type="number"
        onChange={(e) => {
          const value = e.target.value;
          onInputChange('year', value);
        }}
        onWheel={(e) => e.currentTarget.blur()}
        className="w-full p-2 border rounded"
        placeholder="Year"
        min="1900"
        max={new Date().getFullYear()}
        required
      />
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Transmission
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Manual"
              checked={data.transmission === 'Manual'}
              onChange={(e) => onInputChange('transmission', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Manual</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Automatic"
              checked={data.transmission === 'Automatic'}
              onChange={(e) => onInputChange('transmission', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Automatic</span>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Drive Position
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Right"
              checked={data.drivePosition === 'Right'}
              onChange={(e) => onInputChange('drivePosition', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Right Hand Drive</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Left"
              checked={data.drivePosition === 'Left'}
              onChange={(e) => onInputChange('drivePosition', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Left Hand Drive</span>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Number of Seats
        </label>
        <select
          value={data.seats}
          onChange={(e) => onInputChange('seats', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          {[1,2,3,4,5,6,7,8].map(num => (
            <option key={num} value={num}>{num}</option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Alarm System
        </label>
        <select
          value={data.alarm}
          onChange={(e) => onInputChange('alarm', e.target.value)}
          className="w-full p-2 border rounded"
          required
        >
          <option value="Standard Factory Fit">Standard Factory Fit</option>
          <option value="Thatcham">Thatcham</option>
          <option value="No Alarm">No Alarm</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Tracking Device
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Yes"
              checked={data.tracker === 'Yes'}
              onChange={(e) => onInputChange('tracker', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="No"
              checked={data.tracker === 'No'}
              onChange={(e) => onInputChange('tracker', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">No</span>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Car Value
        </label>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
            £
          </span>
          <input
            type="number"
            placeholder="Estimated car value"
            value={data.value}
            onChange={(e) => onInputChange('value', e.target.value)}
            className="w-full p-2 pl-7 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            min="0"
            step="1"
            onWheel={(e) => e.currentTarget.blur()}
            required
          />
        </div>
      </div>
      <input
        type="number"
        placeholder="Annual Mileage"
        value={data.mileage}
        onChange={(e) => onInputChange('mileage', e.target.value)}
        className="w-full p-2 border rounded"
        min="0"
        onWheel={(e) => e.currentTarget.blur()}
        required
      />
      
      <div className="border-t pt-6 mt-6">
        <VehicleOwnershipStep
          data={data.ownership}
          onInputChange={(field, value) => {
            onInputChange('ownership', {
              ...data.ownership,
              [field]: value
            });
          }}
        />
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Is the vehicle modified?
        </label>
        <div className="space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Yes"
              checked={data.isModified === 'Yes'}
              onChange={(e) => onInputChange('isModified', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="No"
              checked={data.isModified === 'No'}
              onChange={(e) => onInputChange('isModified', e.target.value)}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">No</span>
          </label>
        </div>
      </div>
    </div>
  );
}